import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';
import { truncateTextStyle } from '../../../styles/styles';

const { colors, common, spacing, typography } = THEME;

// THEME carries no elevation token, so the sticky edge shadow is named here rather than repeated per cell.
const STICKY_EDGE_SHADOW = '1px 0 4px rgba(0, 0, 0, 0.1)';

export const styleSheet = StyleSheet.create({
	// The outer box is clamped and hidden so the page itself never scrolls sideways; `tableWrapperInner` is
	// the only scroller, which is what lets a table wider than the viewport scroll on its own.
	tableWrapper: {
		boxSizing: 'border-box',
		maxHeight: 'calc(100vh - 218px)',
		maxWidth: '100%',
		minWidth: 0,
		overflow: 'hidden',
	},
	tableWrapperInner: {
		background: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
		boxSizing: 'border-box',
		maxHeight: 'calc(100vh - 218px)',
		overflow: 'auto',
	},

	// The clamp above is the viewport less the Blog Report page's own chrome, so it only fits there. A host
	// that clips and sizes its own content hands the table a height instead.
	fillHost: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
	},
	tableWrapperFill: {
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '100%',
		minHeight: 0,
	},
	tableWrapperInnerFill: {
		flex: 1,
		maxHeight: 'none',
		minHeight: 0,
	},

	// Sticky is driven by scroll position, not by overflow: the classes only attach once the reader has
	// scrolled off an edge, so a table that fits pays nothing.
	rowSticky: {
		position: 'sticky',
		top: 0,
		zIndex: 5,
	},
	headerCellSticky: {
		backgroundColor: colors.hover.light,
		boxShadow: STICKY_EDGE_SHADOW,
		left: 0,
		position: 'sticky',
		zIndex: 10,
	},
	// Drops below the row header so the top-left corner keeps a single owner while both axes are scrolled.
	headerCellStickyRightScroll: {
		zIndex: 6,
	},
	dataCellSticky: {
		backgroundColor: colors.background.light,
		boxShadow: STICKY_EDGE_SHADOW,
		left: 0,
		position: 'sticky',
		zIndex: 2,
	},

	// `HEADER_CELL_TEXT` in DataGrid sets no colour, so a header inherits the dark body ink unless told
	// otherwise. Applied to every column so the header row reads as one band.
	headerCell: {
		color: colors.text.light,
	},

	titleColumn: {
		maxWidth: 450,
		minWidth: 260,
		width: 450,
	},
	titleButton: {
		display: 'block',
		fontWeight: 600,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
		textAlign: 'left',
		width: '100%',
		...truncateTextStyle,
	},
	authorColumn: {
		minWidth: 180,
		width: 220,
	},
	authorName: {
		color: colors.text.main,
	},
	authorEmail: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xs,
		...truncateTextStyle,
	},
	statusColumn: {
		minWidth: 130,
		width: 140,
	},
	// These style objects are handed to both the `th` and the `td`, so centring here aligns header and value
	// together and beats the base cell's `text-align: left`, which is applied before them.
	dateColumn: {
		color: colors.text.light,
		minWidth: 110,
		textAlign: 'center',
		width: 120,
	},
	dateTimeColumn: {
		color: colors.text.light,
		minWidth: 170,
		textAlign: 'center',
		width: 190,
	},

	loading: {
		marginTop: 10,
	},
	loadFailed: {
		alignItems: 'center',
		color: colors.text.main,
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.md,
		padding: '50px 0',
		textAlign: 'center',
	},
	noneFound: {
		color: colors.text.light,
		letterSpacing: 1.1,
		padding: '50px 0',
		textAlign: 'center',
		textTransform: 'uppercase',
	},

	filter: {
		alignItems: 'center',
		color: colors.text.main,
		display: 'flex',
		fontSize: typography.fontSizes.sm,
		gap: spacing.sm,
		justifyContent: 'flex-start',
		marginBottom: 20,
		marginTop: 10,
		width: '100%',
	},
	filterLabel: {
		flexShrink: 0,
		paddingRight: 6,
	},
	// A floor, not a pin: the trigger clips its label with no ellipsis, so a fixed width cuts a name short.
	filterTrigger: {
		background: colors.background.light,
		color: colors.text.main,
		fontSize: typography.fontSizes.sm,
		maxWidth: 320,
		minWidth: 180,
		':has(*) > span': {
			display: 'block',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
	},
});
